import { WidgetTourConfig } from 'partner360/components/widgetTour'
import { MANAGER_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/managerCockpitTourTargets'

import { MANAGER_COCKPIT_TOUR_TRANSLATION_KEYS } from '../../../../configs/managerCockpitTourTranslations'
import { MANAGER_COCKPIT_TILE_IDS } from '../tiles'

const KEYS = MANAGER_COCKPIT_TOUR_TRANSLATION_KEYS.steps

export const MANAGER_COCKPIT_WIDGET_ID = 'manager-cockpit'

/**
 * Ships as the tour until the backend serves records for this widget id, and doubles as the
 * source for them: make it into valid JSON by replacing the TS constants with their values. Step
 * text is translation keys that the renderer resolves, so the backend records carry the same keys
 * and `translatable: true` — without the flag the keys would be rendered as written.
 *
 * The tour walks the Cockpit section only, so it opens by switching to it. Every step from the
 * tiles on carries COLLAPSE_TILE, for the same reason as on the single-partner board: a maximized
 * tile leaves its grid copy mounted at `opacity: 0`, and the tour resolves a target by the first
 * match in document order.
 */
export const MANAGER_COCKPIT_TOURS: WidgetTourConfig[] = [
  {
    id: 'manager-cockpit-overview',
    widgetId: MANAGER_COCKPIT_WIDGET_ID,
    priority: 10,
    enabled: true,
    autoStart: true,
    translatable: true,
    steps: [
      {
        title: KEYS.welcome.title,
        description: KEYS.welcome.description,
        action: { type: 'SET_TAB', payload: 'cockpit' },
      },
      {
        targetId: TOUR_TARGETS.sectionRail,
        title: KEYS.sectionRail.title,
        description: KEYS.sectionRail.description,
      },
      {
        targetId: TOUR_TARGETS.filterPanel,
        title: KEYS.filterPanel.title,
        description: KEYS.filterPanel.description,
      },
      {
        targetId: TOUR_TARGETS.scopePanel,
        title: KEYS.scopePanel.title,
        description: KEYS.scopePanel.description,
      },
      {
        targetId: TOUR_TARGETS.tileRevenue,
        title: KEYS.revenueTile.title,
        description: KEYS.revenueTile.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.tileInitiatives,
        title: KEYS.initiativesTile.title,
        description: KEYS.initiativesTile.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.tileAttention,
        title: KEYS.attentionTile.title,
        description: KEYS.attentionTile.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.tileDocuments,
        title: KEYS.documentsTile.title,
        description: KEYS.documentsTile.description,
        action: { type: 'COLLAPSE_TILE' },
      },

      // The fork: Close ends the tour outright and Next walks into the detailed segment below.
      {
        title: KEYS.overviewFork.title,
        description: KEYS.overviewFork.description,
        nextLabel: KEYS.overviewFork.next,
        dismissLabel: KEYS.overviewFork.dismiss,
        hidePrevious: true,
        action: { type: 'COLLAPSE_TILE' },
      },

      // The detail pass reads the page the way it is laid out: the filters on the left first,
      // then the control panel top to bottom, then each tile opened up.
      {
        targetId: TOUR_TARGETS.filterRtm,
        title: KEYS.filterRtm.title,
        description: KEYS.filterRtm.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.filterOptions,
        title: KEYS.filterOptions.title,
        description: KEYS.filterOptions.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.scopeBar,
        title: KEYS.scopeBar.title,
        description: KEYS.scopeBar.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.scopeFiscalYear,
        title: KEYS.scopeFiscalYear.title,
        description: KEYS.scopeFiscalYear.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      // The plan's state under the year, counted across the partners in view.
      {
        targetId: TOUR_TARGETS.statusCompleteness,
        title: KEYS.statusCompleteness.title,
        description: KEYS.statusCompleteness.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.statusAcknowledgment,
        title: KEYS.statusAcknowledgment.title,
        description: KEYS.statusAcknowledgment.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.statusPlanQuality,
        title: KEYS.statusPlanQuality.title,
        description: KEYS.statusPlanQuality.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.scopePeriod,
        title: KEYS.scopePeriod.title,
        description: KEYS.scopePeriod.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      // Metric and currency render only once a value resolves; a missing chip skips its step.
      {
        targetId: TOUR_TARGETS.scopeMetric,
        title: KEYS.scopeMetric.title,
        description: KEYS.scopeMetric.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.scopeCurrency,
        title: KEYS.scopeCurrency.title,
        description: KEYS.scopeCurrency.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.trainingCentre,
        title: KEYS.trainingCentre.title,
        description: KEYS.trainingCentre.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.programmeTabs,
        title: KEYS.programmeTabs.title,
        description: KEYS.programmeTabs.description,
        action: { type: 'COLLAPSE_TILE' },
      },
      {
        targetId: TOUR_TARGETS.categoryChip,
        title: KEYS.categoryChip.title,
        description: KEYS.categoryChip.description,
        action: { type: 'COLLAPSE_TILE' },
      },

      {
        targetId: TOUR_TARGETS.revenueViewSwitch,
        title: KEYS.revenueDetail.title,
        description: KEYS.revenueDetail.description,
        action: {
          type: 'EXPAND_TILE',
          payload: MANAGER_COCKPIT_TILE_IDS.revenueAndSob,
          view: 'detail',
        },
        // The overlay stretches into place over 180ms, and a transform-only change triggers
        // none of the renderer's observers, so measure it once it has settled.
        waitAfterActionMs: 250,
      },
      {
        targetId: TOUR_TARGETS.tileRevenueExpanded,
        title: KEYS.revenueQuality.title,
        description: KEYS.revenueQuality.description,
        action: {
          type: 'EXPAND_TILE',
          payload: MANAGER_COCKPIT_TILE_IDS.revenueAndSob,
          view: 'quality',
        },
      },
      {
        targetId: TOUR_TARGETS.initiativesViewSwitch,
        title: KEYS.initiativesList.title,
        description: KEYS.initiativesList.description,
        action: {
          type: 'EXPAND_TILE',
          payload: MANAGER_COCKPIT_TILE_IDS.initiatives,
          view: 'initiatives',
        },
        waitAfterActionMs: 250,
      },
      {
        targetId: TOUR_TARGETS.tileInitiativesExpanded,
        title: KEYS.initiativesMdf.title,
        description: KEYS.initiativesMdf.description,
        action: {
          type: 'EXPAND_TILE',
          payload: MANAGER_COCKPIT_TILE_IDS.initiatives,
          view: 'mdf',
        },
      },
      {
        targetId: TOUR_TARGETS.tileInitiativesExpanded,
        title: KEYS.initiativesQuality.title,
        description: KEYS.initiativesQuality.description,
        action: {
          type: 'EXPAND_TILE',
          payload: MANAGER_COCKPIT_TILE_IDS.initiatives,
          view: 'quality',
        },
      },
      {
        targetId: TOUR_TARGETS.tileAttentionExpanded,
        title: KEYS.attentionDetail.title,
        description: KEYS.attentionDetail.description,
        action: { type: 'EXPAND_TILE', payload: MANAGER_COCKPIT_TILE_IDS.attention },
        waitAfterActionMs: 250,
      },
      {
        targetId: TOUR_TARGETS.tileDocumentsExpanded,
        title: KEYS.documentsDetail.title,
        description: KEYS.documentsDetail.description,
        action: { type: 'EXPAND_TILE', payload: MANAGER_COCKPIT_TILE_IDS.documentsAndLinks },
        waitAfterActionMs: 250,
      },
      {
        title: KEYS.finish.title,
        description: KEYS.finish.description,
        action: { type: 'COLLAPSE_TILE' },
      },
    ],
  },
]
